/**
 * Front Office Dashboard
 * Quick access to bookings, check-in/out, and room status
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  Calendar, Search, RefreshCw, Users, DoorOpen, 
  CheckCircle2, Clock, ArrowRightLeft, Plus, UserPlus,
  LogIn, LogOut, AlertTriangle
} from 'lucide-react';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';

export default function FrontOfficeDashboard({ user }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    todayArrivals: 0,
    todayDepartures: 0,
    availableRooms: 0,
    occupiedRooms: 0,
    pendingCheckIns: 0,
    pendingCheckOuts: 0,
  });
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { openTab } = useTabs();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const [roomsRes, bookingsRes] = await Promise.all([
        api.get('/rooms'),
        api.get(`/bookings?date=${today}`).catch(() => ({ data: [] }))
      ]);
      
      const rooms = roomsRes.data || [];
      const bookings = bookingsRes.data || [];
      
      // Calculate stats
      const available = rooms.filter(r => r.status === 'available').length;
      const occupied = rooms.filter(r => r.status === 'occupied').length;
      
      const todayArrivals = bookings.filter(b => 
        b.check_in_date === today && ['confirmed', 'pending'].includes(b.status)
      );
      const todayDepartures = bookings.filter(b => 
        b.check_out_date === today && b.status === 'checked_in'
      );
      
      setStats({
        todayArrivals: todayArrivals.length,
        todayDepartures: todayDepartures.length,
        availableRooms: available,
        occupiedRooms: occupied,
        pendingCheckIns: todayArrivals.length,
        pendingCheckOuts: todayDepartures.length,
      });
      
      setArrivals(todayArrivals.slice(0, 5));
      setDepartures(todayDepartures.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto-refresh every 2 minutes
    const interval = setInterval(loadData, 120000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleNewBooking = () => {
    openTab({
      type: TAB_TYPES.NEW_BOOKING,
      id: `new-booking-${Date.now()}`,
      title: 'New Booking',
      data: {}
    });
  };

  const handleWalkIn = () => {
    openTab({
      type: TAB_TYPES.WALK_IN,
      id: `walk-in-${Date.now()}`,
      title: 'Walk-in Check-in',
      data: {}
    });
  };

  const handleViewBooking = (booking) => {
    openTab({
      type: TAB_TYPES.BOOKING_DETAILS,
      id: `booking-${booking.id}`,
      title: `Booking #${booking.booking_number || booking.id.slice(0, 8)}`,
      data: { bookingId: booking.id }
    });
  };

  const handleQuickSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      // Search by booking number, guest name, or phone
      const res = await api.get(`/bookings/search?q=${encodeURIComponent(searchQuery)}`).catch(() => ({ data: [] }));
      
      if (res.data && res.data.length > 0) {
        handleViewBooking(res.data[0]);
      } else {
        toast.info('No booking found');
      }
    } catch (error) {
      toast.error('Search failed');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="front-office-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Front Office</h1>
          <p className="text-gray-500">Welcome back, {user?.name || 'Staff'}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleNewBooking} className="bg-blue-500 hover:bg-blue-600">
            <Plus className="h-4 w-4 mr-2" />
            New Booking
          </Button>
          <Button onClick={handleWalkIn} variant="outline">
            <UserPlus className="h-4 w-4 mr-2" />
            Walk-in
          </Button>
        </div>
      </div>

      {/* Quick Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by booking #, guest name, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleQuickSearch()}
                className="pl-10"
              />
            </div>
            <Button onClick={handleQuickSearch}>Search</Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <DoorOpen className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">{stats.availableRooms}</p>
                <p className="text-xs text-green-600">Available</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-lg">
                <Users className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.occupiedRooms}</p>
                <p className="text-xs text-red-600">Occupied</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <LogIn className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.todayArrivals}</p>
                <p className="text-xs text-blue-600">Arrivals Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <LogOut className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{stats.todayDepartures}</p>
                <p className="text-xs text-orange-600">Departures Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Arrivals and Departures */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Today's Arrivals */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <LogIn className="h-5 w-5 text-blue-500" />
              Today's Arrivals
              {arrivals.length > 0 && (
                <Badge className="bg-blue-500">{arrivals.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {arrivals.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <CheckCircle2 className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm">No arrivals scheduled</p>
              </div>
            ) : (
              <div className="space-y-3">
                {arrivals.map((booking) => (
                  <div 
                    key={booking.id}
                    className="flex items-center justify-between p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100"
                    onClick={() => handleViewBooking(booking)}
                  >
                    <div>
                      <p className="font-medium">{booking.guest_name || 'Guest'}</p>
                      <p className="text-xs text-gray-500">Room: {booking.room_number || 'TBA'}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="bg-white">
                        {booking.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's Departures */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <LogOut className="h-5 w-5 text-orange-500" />
              Today's Departures
              {departures.length > 0 && (
                <Badge className="bg-orange-500">{departures.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {departures.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <CheckCircle2 className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm">No departures today</p>
              </div>
            ) : (
              <div className="space-y-3">
                {departures.map((booking) => (
                  <div 
                    key={booking.id}
                    className="flex items-center justify-between p-3 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100"
                    onClick={() => handleViewBooking(booking)}
                  >
                    <div>
                      <p className="font-medium">{booking.guest_name || 'Guest'}</p>
                      <p className="text-xs text-gray-500">Room: {booking.room_number || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="bg-white text-orange-700">
                        Checkout Due
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => window.location.href = '/admin/room-availability'}
            >
              <DoorOpen className="h-6 w-6 text-blue-500" />
              <span className="text-xs">Room Status</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => window.location.href = '/admin/bookings'}
            >
              <Calendar className="h-6 w-6 text-green-500" />
              <span className="text-xs">All Bookings</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => window.location.href = '/admin/guests'}
            >
              <Users className="h-6 w-6 text-purple-500" />
              <span className="text-xs">Guests</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => window.location.href = '/admin/service-orders'}
            >
              <ArrowRightLeft className="h-6 w-6 text-orange-500" />
              <span className="text-xs">Service Orders</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
